import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { timeDiffMinutes } from "@/lib/format";
import { useMyTasks, type MyTask } from "@/hooks/use-my-tasks";
import { useInternalProjectTasks, internalTaskToMyTask } from "@/hooks/use-internal-project-tasks";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  useUpdateTimeEntry,
  type TimeEntry,
} from "@/hooks/use-time-registration";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCompanyHolidays } from "@/hooks/use-company-holidays";
import { isWeekendOrHoliday, suggestWeekendHolidayHours } from "@/lib/overtime-calc";
import {
  requireCommentForTrackedProject,
  commentRequiredForProject,
} from "@/lib/time-entry-validation";

interface Props {
  entry: TimeEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Draft {
  taskKey: string; // `${project_id}|${budget_line_id}` for combobox value match
  project_id: string | null;
  budget_line_id: string | null;
  task_description: string;
  date: string; // YYYY-MM-DD
  fromTime: string; // HH:MM
  toTime: string; // HH:MM
  nextDay: boolean; // end time belongs to the day after `date` (crosses midnight)
  comment: string;
  weekend_holiday_hours: number;
  overnight_count: number;
  overtime_comment: string;
}

/**
 * VIB-704: Felles dialog for full redigering av en time_entries-rad.
 * Bruker fra dag/uke/måned-views via blyant-knapp på hver registrering.
 * Lagrer kun endrede felt (diff-only) via useUpdateTimeEntry.
 */
export function RegistrationEditDialog({ entry, open, onOpenChange }: Props) {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;
  const { data: myTasks } = useMyTasks(userId);
  const tasks = myTasks?.tasks ?? [];
  const updateEntry = useUpdateTimeEntry();

  // VIB-1208: internprosjekt-oppgaver er velgbare i samme combobox som
  // kundeprosjekt-oppgaver.
  const { data: internalProjectTasksData } = useInternalProjectTasks();
  const internalTaskItems = (internalProjectTasksData ?? []).map(internalTaskToMyTask);
  const allPickableTasks = [...tasks, ...internalTaskItems];

  const [draft, setDraft] = useState<Draft | null>(null);

  // VIB-790/791: fetch project fields for comment requirement (internal + T&M)
  const currentProjectId = draft?.project_id ?? null;
  const { data: projectMeta } = useQuery({
    queryKey: ["project-comment-meta", currentProjectId],
    queryFn: async (): Promise<{ is_internal: boolean; project_type: string | null }> => {
      if (!currentProjectId) return { is_internal: false, project_type: null };
      const { data, error } = await supabase
        .from("projects")
        .select("is_internal, project_type")
        .eq("id", currentProjectId)
        .single();
      if (error) return { is_internal: false, project_type: null };
      return {
        is_internal: (data as { is_internal: boolean }).is_internal ?? false,
        project_type: (data as { project_type: string | null }).project_type ?? null,
      };
    },
    enabled: !!currentProjectId,
    staleTime: 5 * 60 * 1000,
  });
  const commentRequired = commentRequiredForProject(projectMeta);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);
  // Year follows the entry's date (editing a December entry in January must
  // still see that year's holidays), falling back to the current year.
  const entryYear = entry
    ? new Date(entry.start_date).getFullYear()
    : new Date().getFullYear();
  const { data: holidays } = useCompanyHolidays(entryYear);

  useEffect(() => {
    if (entry && open) {
      const start = new Date(entry.start_date);
      const end = new Date(start.getTime() + entry.duration_seconds * 1000);
      const date = format(start, "yyyy-MM-dd");
      const fromTime = format(start, "HH:mm");
      const toTime = format(end, "HH:mm");
      // Existing entry already crosses midnight if the end lands on a later date.
      const nextDay = format(end, "yyyy-MM-dd") !== date;
      // VIB-1224: forhåndsutfyll helg-/
      // helligdagstimer når feltet aldri er satt og dagen er helg/helligdag.
      // Kun et FORSLAG i skjemaet — ingenting lagres før brukeren trykker
      // Lagre (enforce_month_lock avviser fortsatt låste måneder i DB).
      const suggested =
        entry.weekend_holiday_hours == null
          ? suggestWeekendHolidayHours(entry.start_date, entry.duration_seconds, holidays ?? new Set<string>())
          : null;
      setDraft({
        taskKey: `${entry.project_id ?? ""}|${entry.budget_line_id ?? ""}`,
        project_id: entry.project_id,
        budget_line_id: entry.budget_line_id,
        task_description: entry.task_description,
        date,
        fromTime,
        toTime,
        nextDay,
        comment: entry.comment ?? "",
        weekend_holiday_hours: entry.weekend_holiday_hours ?? suggested ?? 0,
        overnight_count: entry.overnight_count ?? 0,
        overtime_comment: entry.overtime_comment ?? "",
      });
      setAdvancedOpen(
        (entry.weekend_holiday_hours ?? 0) > 0 ||
          (entry.overnight_count ?? 0) > 0 ||
          !!entry.overtime_comment ||
          (suggested ?? 0) > 0 ||
          isWeekendOrHoliday(entry.start_date, holidays ?? new Set<string>()),
      );
    }
  }, [entry, open, holidays]);

  if (!entry || !draft) return null;

  function pickTask(task: MyTask) {
    setDraft((d) =>
      d
        ? {
            ...d,
            taskKey: `${task.project_id}|${task.budget_line_id}`,
            project_id: task.project_id,
            budget_line_id: task.budget_line_id || null,
            task_description: task.task_description,
          }
        : d,
    );
    setTaskPickerOpen(false);
  }

  function handleSave() {
    if (!entry || !draft) return;

    // VIB-790/791: krev kommentar for internprosjekt og T&M-prosjekt
    const commentError = requireCommentForTrackedProject(projectMeta ?? null, draft.comment);
    if (commentError) {
      toast.error(commentError);
      return;
    }

    // Bygg ny start_date (combined date + fromTime, local time → ISO).
    const newStart = new Date(`${draft.date}T${draft.fromTime}:00`);
    if (isNaN(newStart.getTime())) {
      toast.error("Ugyldig dato/tid");
      return;
    }
    // Beregn duration fra fromTime til toTime (nextDay = krysser midnatt)
    const durationMin = timeDiffMinutes(draft.fromTime, draft.toTime, draft.nextDay);
    if (!Number.isFinite(durationMin) || durationMin <= 0) {
      toast.error(
        draft.nextDay
          ? "Ugyldig tidsrom"
          : "Til-tid må være etter fra-tid (huk av «Til neste dag» hvis den går over midnatt)",
      );
      return;
    }
    const newDurationSec = durationMin * 60;
    const newStartISO = newStart.toISOString();

    // Diff-only patch
    const updates: Parameters<typeof updateEntry.mutate>[0]["updates"] = {};
    if (newStartISO !== entry.start_date) updates.start_date = newStartISO;
    if (newDurationSec !== entry.duration_seconds) updates.duration_seconds = newDurationSec;
    if (draft.project_id !== entry.project_id) updates.project_id = draft.project_id;
    if (draft.budget_line_id !== entry.budget_line_id) updates.budget_line_id = draft.budget_line_id;
    if (draft.task_description !== entry.task_description) updates.task_description = draft.task_description;
    if ((draft.comment.trim() || null) !== entry.comment) updates.comment = draft.comment.trim() || null;
    if (draft.weekend_holiday_hours !== (entry.weekend_holiday_hours ?? 0)) {
      updates.weekend_holiday_hours = draft.weekend_holiday_hours || null;
    }
    if (draft.overnight_count !== (entry.overnight_count ?? 0)) {
      updates.overnight_count = draft.overnight_count || null;
    }
    if ((draft.overtime_comment.trim() || null) !== entry.overtime_comment) {
      updates.overtime_comment = draft.overtime_comment.trim() || null;
    }

    if (Object.keys(updates).length === 0) {
      onOpenChange(false);
      return;
    }

    updateEntry.mutate(
      { entryId: entry.id, updates },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const selectedTask = allPickableTasks.find(
    (t) => `${t.project_id}|${t.budget_line_id}` === draft.taskKey,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Rediger timeføring</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Prosjekt og oppgave</Label>
            <Popover open={taskPickerOpen} onOpenChange={setTaskPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                >
                  {selectedTask ? (
                    <span className="truncate">
                      {selectedTask.project_name} — {selectedTask.task_description}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      {draft.task_description || "Velg oppgave…"}
                    </span>
                  )}
                  <SrIcon name="chevrons-up-down" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Søk prosjekt eller oppgave…" />
                  <CommandList>
                    <CommandEmpty>Ingen treff</CommandEmpty>
                    <CommandGroup>
                      {tasks.map((t) => (
                        <CommandItem
                          key={`${t.project_id}|${t.budget_line_id}`}
                          value={`${t.project_name} ${t.task_description}`}
                          onSelect={() => pickTask(t)}
                        >
                          <SrIcon
                            name="check"
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedTask?.id === t.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <span className="truncate">
                            {t.project_name} — {t.task_description}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {/* VIB-1208: internprosjekt-oppgaver */}
                    {internalTaskItems.length > 0 && (
                      <CommandGroup heading="Interne oppgaver">
                        {internalTaskItems.map((t) => (
                          <CommandItem
                            key={`${t.project_id}|${t.budget_line_id}`}
                            value={`${t.project_name} ${t.task_description}`}
                            onSelect={() => pickTask(t)}
                          >
                            <SrIcon
                              name="check"
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedTask?.id === t.id ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <span className="truncate">
                              {t.project_name} — {t.task_description}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Dato</Label>
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Fra</Label>
              <Input
                type="time"
                value={draft.fromTime}
                onChange={(e) => setDraft({ ...draft, fromTime: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Til</Label>
              <Input
                type="time"
                value={draft.toTime}
                onChange={(e) => setDraft({ ...draft, toTime: e.target.value })}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.nextDay}
              onChange={(e) => setDraft({ ...draft, nextDay: e.target.checked })}
              className="h-4 w-4 accent-primary"
            />
            Til-tid er neste dag (over midnatt)
          </label>

          <div>
            <Label className="text-xs">
              Kommentar{commentRequired ? " (påkrevd)" : " (valgfritt)"}
            </Label>
            <Textarea
              value={draft.comment}
              onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
              rows={2}
              placeholder={commentRequired ? "Beskriv hva du brukte tiden på…" : "Notater til registreringen…"}
            />
          </div>

          {/* Collapsible "Tilleggsfelter" */}
          <div className="border-t pt-3">
            {/* VIB-1287: overnatting synlig på alle føringer (Erik 15.07) */}
            <div>
              <Label className="text-xs">Overnatting (netter)</Label>
              <Input
                type="number"
                min={0}
                value={String(draft.overnight_count)}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    overnight_count: Number(e.target.value) || 0,
                  })
                }
                aria-label="Overnatting (netter)"
              />
            </div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <SrIcon
                name={advancedOpen ? "chevron-down" : "chevron-right"}
                className="w-3 h-3"
              />
              Tilleggsfelter (overtid, helg)
            </button>
            {advancedOpen && (
              <div className="space-y-3 mt-3 pl-5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Helg/helligdag (t)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min={0}
                      value={String(draft.weekend_holiday_hours)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          weekend_holiday_hours: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </div>

                </div>
                <div>
                  <Label className="text-xs">Overtid/-kommentar</Label>
                  <Textarea
                    value={draft.overtime_comment}
                    onChange={(e) =>
                      setDraft({ ...draft, overtime_comment: e.target.value })
                    }
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSave} disabled={updateEntry.isPending}>
            {updateEntry.isPending && (
              <SrIcon name="loader2" className="w-4 h-4 mr-2 animate-spin" />
            )}
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
